import { useState, useEffect, forwardRef } from "react";
import { Box, Typography, Link, Avatar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatchApp } from "../../lib/redux";
import { startGetProgressValues } from "../../actions/ui";
import { off } from "firebase/database";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  BUTTON_SAVE_AND_PUBLISH,
  ALERT_GENERIC_ERROR_TITLE,
  GENERATING_CAMPAIGN,
  CAMPAIGN_GENERATED_ERROR_DESCRIPTION,
  AD_GROUP,
  AD,
  ASSETS_GROUP,
  CAMPAIGN,
} from "../../i18n/keysTranslations";
import { errorCodeToText } from "../../utils/errors";
import linkifyHtml from "linkify-html";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import { logoPlatform } from "../../utils/string";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import { getCreativeJobAlertConfig } from "../../utils/creativeJobAlertConfig";
import { retryGenerateInitialCampaignFromAlert } from "../Modal/ModalCreateAdsCampaign/startGenerateInitialCampaign";
import { buildResumeCampaignOnboardingUrl } from "../Modal/ModalCreateAdsCampaign/createAdsCampaignWizardUtils";

const getTaskVisualStatus = ({ isError, isSuccess }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  return "inProgress";
};

const CampaignAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const [progress, setProgress] = useState(alert.progress || 0);
  const [reference, setReference] = useState(null);
  const [isRetryingGeneration, setIsRetryingGeneration] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const {
    id: notificationId,
    campaignID,
    jobID,
    isCreating,
    status,
    message,
    failedCampaigns,
    errorsCampaigns,
    currentCampaign,
    buttonAction,
    isGenerating,
    platform,
    platformGeneratingData,
    newPlatforms,
    messageKey,
    jobType,
    timestamp,
  } = alert;

  const { userID } = dispatch(getPathDatabaseByRole());
  const isError = status === ALERTS_ASYNC_STATUS.ERROR;
  const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isCreatingState = isCreating && status === ALERTS_ASYNC_STATUS.LOADING;
  const isInProgressStatus =
    status === ALERTS_ASYNC_STATUS.LOADING ||
    status === ALERTS_ASYNC_STATUS.GENERATING;
  const isGeneratingInProgress =
    !isError &&
    !isSuccess &&
    (isGenerating || isInProgressStatus || isCreatingState);
  const isInitialGenerationError =
    isError &&
    jobType === CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform &&
    buttonAction !== "addPlatform";

  const getTranslatedMessage = () => {
    const config = getCreativeJobAlertConfig(jobType);

    if (isError) {
      // Las alertas sin jobType (publicación/actualización) no usan títulos de creativos
      if (!jobType) {
        return message || t(ALERT_GENERIC_ERROR_TITLE);
      }
      return t(config.messageKeys.error);
    }
    if (isSuccess) {
      return message || t(config.messageKeys.success);
    }
    if (message) {
      return message;
    }
    if (messageKey) {
      return t(messageKey);
    }
    return t(config.messageKeys.loading || GENERATING_CAMPAIGN);
  };

  const {
    loaderTimeMs: generatingLoaderTimeMs,
    onProgressChange: onGeneratingProgressChange,
    seedProgress: generatingSeedProgress,
    formattedRemainingTime,
  } = useCreativeJobProgressLoader({
    storageKey: jobID ?? campaignID,
    jobType: CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform,
    isTerminal: !isGeneratingInProgress,
    runId: timestamp,
  });
  const additionalParams =
    isError && buttonAction === BUTTON_SAVE_AND_PUBLISH
      ? "&doSync=false&showDraft=true"
      : "";
  const isAddPlatform = buttonAction === "addPlatform";

  const campaignToShow = currentCampaign;

  // Optimized functions to get campaign data
  const getCampaignNameByPlatform = (platform) => {
    return campaignToShow?.platformsData?.[platform]?.name || "";
  };

  // Get translated error message for a platform
  const getTranslatedErrorMessage = (error, platformData) => {
    if (!error) return "";

    const code = error?.code;
    const pageID = platformData?.pageID;
    const businessID = platformData?.businessID;

    return errorCodeToText(code) === code
      ? error?.message
      : errorCodeToText(code, { pageID, businessID });
  };

  // Get text scope for error details
  const getTextScope = (error, campaign, platform) => {
    const additionalData = error?.additionalData || {};
    const scope = additionalData?.scope || "";

    if (scope === "campaign") {
      const name = getCampaignNameByPlatform(platform);
      return `<div><strong>${t(CAMPAIGN)}</strong>: ${name}</div>`;
    }

    const assetsGroupID = additionalData?.assetsGroupID;
    const adGroupID = additionalData?.adGroupID;
    const adID = additionalData?.adID;

    if (!adGroupID && !adID && !assetsGroupID) return "";

    // Helper function to get name or fallback to index number
    const getNameOrFallback = (name, index) => {
      if (name) return name;
      if (index !== undefined && index !== null && index >= 0) {
        return index + 1;
      }
      return "";
    };

    if (assetsGroupID) {
      const assetsGroupIndex = campaign?.assetsGroups?.findIndex(
        (assetsGroup) =>
          assetsGroup.id === assetsGroupID ||
          assetsGroup.idPlatform === assetsGroupID
      );
      if (assetsGroupIndex >= 0) {
        const assetsGroup = campaign.assetsGroups[assetsGroupIndex];
        const assetsGroupName = getNameOrFallback(
          assetsGroup.name,
          assetsGroupIndex
        );
        return `<div><strong>${t(ASSETS_GROUP)}</strong>: ${assetsGroupName}</div>`;
      }
      return "";
    }

    if (adID && adGroupID) {
      const adGroupIndex = campaign?.adsGroups?.findIndex(
        (adGroup) =>
          adGroup.id === adGroupID || adGroup.idPlatform === adGroupID
      );
      if (adGroupIndex >= 0) {
        const adGroup = campaign.adsGroups[adGroupIndex];
        const adIndex = adGroup.ads?.findIndex(
          (ad) => ad.id === adID || ad.idPlatform === adID
        );
        if (adIndex >= 0) {
          const ad = adGroup.ads[adIndex];
          const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
          const adName = getNameOrFallback(ad.name, adIndex);
          return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div><div><strong>${t(AD)}</strong>: ${adName}</div>`;
        }
        const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
        return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div>`;
      }
      return "";
    }

    if (adGroupID) {
      const adGroupIndex = campaign?.adsGroups?.findIndex(
        (adGroup) =>
          adGroup.id === adGroupID || adGroup.idPlatform === adGroupID
      );
      if (adGroupIndex >= 0) {
        const adGroup = campaign.adsGroups[adGroupIndex];
        const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
        return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div>`;
      }
      return "";
    }

    return "";
  };

  // Build path with scope parameters from additionalData
  const buildPathWithScope = (
    campaignID,
    platform,
    additionalData,
    additionalParams
  ) => {
    let path = `/ads-campaign?campaignID=${campaignID}`;

    // Add platform if available
    const errorPlatform = additionalData?.platform || platform;
    if (errorPlatform) {
      path += `&platform=${errorPlatform}`;
    }

    // Add scope parameters based on additionalData
    if (additionalData) {
      const { adGroupID, adID, assetsGroupID } = additionalData;

      // For Google: add assetsGroupID if present
      if (assetsGroupID) {
        path += `&assetsGroupID=${assetsGroupID}`;
      }

      // For Meta/other platforms: add adGroupID and adID if present
      if (adGroupID) {
        path += `&adGroupID=${adGroupID}`;
      }
      if (adID) {
        path += `&adID=${adID}`;
      }
    }

    // Add additional params (doSync, showDraft, etc.)
    if (additionalParams) {
      path += additionalParams;
    }

    return path;
  };

  // Get campaign errors for a specific platform
  const getCampaignErrorsByPlatform = (platform) => {
    if (!errorsCampaigns?.[platform]) return null;

    const error = errorsCampaigns[platform];
    const platformData = campaignToShow?.platformsData?.[platform];

    return {
      code: error?.code,
      message: getTranslatedErrorMessage(error, platformData),
      scope: getTextScope(error, campaignToShow, platform),
      additionalData: error?.additionalData,
    };
  };

  // Handle navigation to campaign details
  const onCampaignClick = (path) => {
    if (campaignID) {
      navigate(path);
    }
  };

  const isGenerationProgressLocked =
    isGeneratingInProgress &&
    !isCreatingState &&
    (Boolean(isGenerating) ||
      jobType === CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform);

  const openAlertDestination = () => {
    if (isGenerationProgressLocked) {
      return;
    }

    if (!campaignID) {
      return;
    }

    if (isInitialGenerationError) {
      const alertID = jobID || notificationId || campaignID;
      onCampaignClick(buildResumeCampaignOnboardingUrl(alertID));
      return;
    }

    let path = `/ads-campaign?campaignID=${campaignID}${additionalParams}`;

    if (isError && errorsCampaigns) {
      const firstErrorPlatform =
        failedCampaigns && failedCampaigns.length > 0
          ? failedCampaigns[0]
          : Object.keys(errorsCampaigns)[0];

      if (firstErrorPlatform && errorsCampaigns[firstErrorPlatform]) {
        const error = errorsCampaigns[firstErrorPlatform];
        const additionalData = error?.additionalData || {};
        path = buildPathWithScope(
          campaignID,
          firstErrorPlatform,
          additionalData,
          additionalParams
        );
      }
    }

    onCampaignClick(path);

    const isGeneratingCampaignAlert =
      jobType === CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform;

    if (isGeneratingCampaignAlert) {
      return;
    }

    if (isSuccess) {
      onClose(notificationId || jobID || campaignID);
    }
  };

  const onAlertClick = (event) => {
    if (event?.target?.closest?.("button")) {
      return;
    }

    openAlertDestination();
  };

  // Update progress based on received data
  const onUpdateProgress = (data = {}) => {
    const { step, totalSteps } = data;

    if (!step || !totalSteps) return;

    let newProgress = Math.min((step / totalSteps) * 100, 100);
    // Allow progress to update even if it's less than current progress
    // This is necessary for retry scenarios where the process starts over
    setProgress(newProgress);
  };

  // Render campaigns list with links and errors
  const renderCampaignsList = (campaigns, isErrorList = false) => {
    if (!campaigns || campaigns.length === 0) return null;

    const platformsData = campaignToShow?.platformsData;
    if (!platformsData && !isErrorList) return null;

    const platformsToShow = isErrorList
      ? campaigns
      : Object.keys(platformsData || {});

    return (
      <Box>
        {platformsToShow.map((platform, idx) => {
          const campaignErrors = getCampaignErrorsByPlatform(platform);
          return (
            <Box
              key={platform}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                mt: idx > 0 ? 0.5 : 0,
              }}
            >
              <Link
                variant="body2"
                onClick={() => {
                  if (campaignID) {
                    const error = errorsCampaigns?.[platform];
                    const additionalData = error?.additionalData || {};
                    const path = buildPathWithScope(
                      campaignID,
                      platform,
                      additionalData,
                      additionalParams
                    );
                    onCampaignClick(path);
                  }
                }}
                sx={{
                  textDecoration: campaignID ? "underline" : "none",
                  cursor: campaignID ? "pointer" : "default",
                  display: "block",
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  color: "text.primary",
                }}
              >
                {`${platform.toUpperCase()} - ${getCampaignNameByPlatform(platform)}`}
              </Link>
              {isError && campaignErrors && (
                <TaskAlertErrorMessage>
                  {campaignErrors.scope && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        width: "100%",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: campaignErrors.scope,
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: "100%",
                      color: "text.primary",
                      flex: 1,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-line",
                      "& *": {
                        p: "revert",
                        m: "revert",
                        listStyle: "revert",
                        textDecoration: "revert",
                      },
                      "& a": {
                        textDecoration: "underline",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: linkifyHtml(
                        (campaignErrors.message || "")?.replace(
                          /\n/g,
                          "<br />"
                        ),
                        {
                          rel: "noopener noreferrer",
                          target: "_blank",
                          style: "color: #00A3FF",
                        }
                      ),
                    }}
                  />
                </TaskAlertErrorMessage>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderConditionalCampaignsList = () => {
    if (
      isError &&
      !isAddPlatform &&
      failedCampaigns &&
      failedCampaigns.length > 0
    ) {
      return renderCampaignsList(failedCampaigns, true);
    }

    const generationPlatform =
      platform || platformGeneratingData?.platform || newPlatforms?.[0];

    if (isError && generationPlatform && !isInitialGenerationError) {
      return renderCampaignsList([generationPlatform], true);
    }

    if (!isError && !isAddPlatform) {
      return renderCampaignsList();
    }

    return null;
  };

  const renderAlertDetails = () => {
    if (!isError) {
      return null;
    }

    const campaignsList = isInitialGenerationError
      ? null
      : renderConditionalCampaignsList();

    const generationErrorDescription = isInitialGenerationError
      ? t(CAMPAIGN_GENERATED_ERROR_DESCRIPTION)
      : message &&
          jobType === CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform
        ? message
        : null;

    if (!campaignsList && !generationErrorDescription) {
      return null;
    }

    return (
      <>
        {campaignsList}
        {generationErrorDescription ? (
          <TaskAlertErrorMessage>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: "0.75rem" }}
            >
              {generationErrorDescription}
            </Typography>
          </TaskAlertErrorMessage>
        ) : null}
      </>
    );
  };

  const onRetryGeneration = async (event) => {
    event?.stopPropagation?.();
    if (isRetryingGeneration) return;
    setIsRetryingGeneration(true);
    try {
      await dispatch(retryGenerateInitialCampaignFromAlert(alert));
    } finally {
      setIsRetryingGeneration(false);
    }
  };

  const renderButtons = () => (
    <TaskAlertDiscardActions
      onStartDiscard={() => onClose(notificationId || jobID || campaignID)}
      onView={isSuccess ? openAlertDestination : null}
      onRetry={isInitialGenerationError ? onRetryGeneration : null}
      retryDisabled={isRetryingGeneration}
    />
  );

  // Renders the main message with animated icon if applicable
  const renderMainMessage = () => (
    <>
      {isGenerating && platform && (
        <Avatar
          src={logoPlatform(platform)}
          variant="square"
          sx={{
            width: 30,
            height: 30,
            mb: 0.2,
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
      )}{" "}
      {getTranslatedMessage()}
    </>
  );

  // Effect to handle progress tracking and cleanup
  useEffect(() => {
    let timeout1, timeout2;

    // Clean previous listener if exists
    if (reference) {
      off(reference);
      setReference(null);
    }

    if (isCreatingState && campaignID && userID) {
      setProgress(0);

      const progressID = `processProgress/${userID}/${campaignID}/`;

      dispatch(
        startGetProgressValues({
          progressID,
          onReferenceAvailable: (ref) => setReference(ref),
          onCallbackUpdateProgress: onUpdateProgress,
        })
      );

      timeout1 = setTimeout(() => {
        setProgress((prevProgress) =>
          prevProgress < 5 ? prevProgress + 5 : prevProgress
        );
      }, 2000);

      timeout2 = setTimeout(() => {
        setProgress((prevProgress) =>
          prevProgress < 10 ? prevProgress + 5 : prevProgress
        );
      }, 4000);
    } else if ((isSuccess || isError) && (campaignID || isSuccess)) {
      setProgress(100);
    } else {
      setProgress(0);
    }

    return () => {
      if (timeout1) clearTimeout(timeout1);
      if (timeout2) clearTimeout(timeout2);
      if (reference) {
        off(reference);
      }
    };
    // eslint-disable-next-line
  }, [
    isCreatingState,
    isSuccess,
    isError,
    campaignID,
    userID,
    dispatch,
    message,
  ]);

  const taskVisualStatus = getTaskVisualStatus({ isError, isSuccess });

  return (
    <TaskAlertCard
      ref={ref}
      status={taskVisualStatus}
      label={renderMainMessage()}
      estimatedTime={isGeneratingInProgress ? formattedRemainingTime : null}
      progress={progress}
      onClick={isGenerationProgressLocked ? undefined : onAlertClick}
      cursor={!isGenerationProgressLocked && campaignID ? "pointer" : "default"}
      showProgressBar={!isGeneratingInProgress}
      showProgressPercent={!isGeneratingInProgress}
      progressSlot={
        isGeneratingInProgress ? (
          <LinearLoaderWithTime
            key={timestamp ?? notificationId ?? campaignID}
            isAlert={true}
            labelSide="bottom"
            time={generatingLoaderTimeMs}
            progress={generatingSeedProgress}
            autoIncrement={true}
            max={99}
            onProgressChange={onGeneratingProgressChange}
          />
        ) : null
      }
      details={renderAlertDetails()}
      actions={isError || isSuccess ? renderButtons() : null}
    />
  );
});

CampaignAlertItem.displayName = "CampaignAlertItem";

export default CampaignAlertItem;
